import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Crop, Pen, Eraser, Undo2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';

const COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#8b5cf6', '#f97316', '#ec4899', '#000000', '#ffffff'];
const MAX_CANVAS_DIM = 1600;
const HISTORY_LIMIT = 20;

// Loads the source image for editing, retrying without crossOrigin if the
// CORS-tagged request is rejected outright (e.g. an R2 bucket with no CORS
// policy). A crossOrigin-less load still displays fine but taints the canvas,
// so exporting from it is caught and surfaced later in handleSave.
function loadEditableImage(src, onLoad, onError) {
  let cancelled = false;
  const attempt = (withCors) => {
    const img = new Image();
    if (withCors) img.crossOrigin = 'anonymous';
    img.onload = () => { if (!cancelled) onLoad(img); };
    img.onerror = () => {
      if (cancelled) return;
      if (withCors) attempt(false);
      else onError();
    };
    img.src = src;
  };
  attempt(true);
  return () => { cancelled = true; };
}

export default function ImageEditorModal({ open, onOpenChange, imageUrl, onSave }) {
  const canvasRef = useRef(null);
  const historyRef = useRef([]);
  const originalDataRef = useRef(null);
  const dragStartRef = useRef(null);
  const isDrawingRef = useRef(false);

  const [mode, setMode] = useState('crop');
  const [color, setColor] = useState('#ef4444');
  const [brushSize, setBrushSize] = useState(5);
  const [isEraser, setIsEraser] = useState(false);
  const [ready, setReady] = useState(false);
  const [cropRect, setCropRect] = useState(null);
  const [canvasBox, setCanvasBox] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!open || !imageUrl) return;
    setReady(false);
    setCropRect(null);
    setMode('crop');
    historyRef.current = [];

    const drawToCanvas = (img) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      let { width, height } = img;
      if (Math.max(width, height) > MAX_CANVAS_DIM) {
        const scale = MAX_CANVAS_DIM / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      setCanvasBox({ width, height });
      try {
        originalDataRef.current = canvas.toDataURL('image/png');
      } catch {
        originalDataRef.current = null;
      }
      setReady(true);
    };

    return loadEditableImage(imageUrl, drawToCanvas, () => {
      toast.error('Could not load image for editing');
    });
  }, [open, imageUrl]);

  const pushHistory = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      historyRef.current.push(canvas.toDataURL('image/png'));
      if (historyRef.current.length > HISTORY_LIMIT) historyRef.current.shift();
    } catch {
      // Tainted canvas — undo history unavailable, edits still apply live.
    }
  }, []);

  const restoreFrom = useCallback((dataUrl) => {
    const canvas = canvasRef.current;
    if (!dataUrl || !canvas) return;
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      setCanvasBox({ width: img.width, height: img.height });
    };
    img.src = dataUrl;
  }, []);

  const undo = useCallback(() => {
    const last = historyRef.current.pop();
    if (!last) return;
    setCropRect(null);
    restoreFrom(last);
  }, [restoreFrom]);

  const resetAll = useCallback(() => {
    if (!originalDataRef.current) return;
    historyRef.current = [];
    setCropRect(null);
    restoreFrom(originalDataRef.current);
  }, [restoreFrom]);

  const getCanvasPoint = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const handlePointerDown = (e) => {
    if (!ready) return;
    const pt = getCanvasPoint(e);
    e.currentTarget.setPointerCapture(e.pointerId);

    if (mode === 'draw') {
      pushHistory();
      isDrawingRef.current = true;
      const ctx = canvasRef.current.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = isEraser ? brushSize * 3 : brushSize;
      ctx.strokeStyle = isEraser ? 'rgba(0,0,0,1)' : color;
      ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
      ctx.beginPath();
      ctx.moveTo(pt.x, pt.y);
    } else {
      dragStartRef.current = pt;
      setCropRect({ x: pt.x, y: pt.y, w: 0, h: 0 });
    }
  };

  const handlePointerMove = (e) => {
    if (!ready) return;
    if (mode === 'draw' && isDrawingRef.current) {
      const pt = getCanvasPoint(e);
      const ctx = canvasRef.current.getContext('2d');
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
    } else if (mode === 'crop' && dragStartRef.current) {
      const pt = getCanvasPoint(e);
      const start = dragStartRef.current;
      setCropRect({
        x: Math.min(start.x, pt.x),
        y: Math.min(start.y, pt.y),
        w: Math.abs(pt.x - start.x),
        h: Math.abs(pt.y - start.y),
      });
    }
  };

  const handlePointerUp = () => {
    isDrawingRef.current = false;
    dragStartRef.current = null;
  };

  const applyCrop = () => {
    const canvas = canvasRef.current;
    if (!canvas || !cropRect || cropRect.w < 4 || cropRect.h < 4) return;
    pushHistory();
    const { x, y, w, h } = cropRect;
    const cw = Math.round(w);
    const ch = Math.round(h);
    const cropped = document.createElement('canvas');
    cropped.width = cw;
    cropped.height = ch;
    cropped.getContext('2d').drawImage(canvas, x, y, w, h, 0, 0, cw, ch);
    canvas.width = cw;
    canvas.height = ch;
    canvas.getContext('2d').drawImage(cropped, 0, 0);
    setCanvasBox({ width: cw, height: ch });
    setCropRect(null);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const dataUrl = canvas.toDataURL('image/webp', 0.85);
      onSave({ dataUrl, width: canvas.width, height: canvas.height });
    } catch {
      toast.error("Can't save — this image can't be edited in-browser (cross-origin restriction)");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-no-drag onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Edit image</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant={mode === 'crop' ? 'default' : 'outline'} size="sm" onClick={() => setMode('crop')}>
            <Crop className="h-4 w-4 mr-1" /> Crop
          </Button>
          <Button variant={mode === 'draw' ? 'default' : 'outline'} size="sm" onClick={() => setMode('draw')}>
            <Pen className="h-4 w-4 mr-1" /> Draw
          </Button>

          {mode === 'draw' && (
            <div className="flex items-center gap-2 ml-1">
              <Button variant={!isEraser ? 'secondary' : 'ghost'} size="icon" className="h-8 w-8" onClick={() => setIsEraser(false)} title="Pen">
                <Pen className="h-4 w-4" />
              </Button>
              <Button variant={isEraser ? 'secondary' : 'ghost'} size="icon" className="h-8 w-8" onClick={() => setIsEraser(true)} title="Eraser">
                <Eraser className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-1">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`w-5 h-5 rounded-full border-2 transition-transform ${color === c ? 'border-primary scale-110' : 'border-transparent'}`}
                    style={{ background: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
              <Slider value={[brushSize]} onValueChange={(v) => setBrushSize(v[0])} min={1} max={30} step={1} className="w-24" />
            </div>
          )}

          {mode === 'crop' && (
            <span className="text-xs text-muted-foreground">
              {cropRect ? 'Drag handles adjusted — click Apply to crop' : 'Drag on the image to select a crop area'}
            </span>
          )}
          {mode === 'crop' && cropRect && cropRect.w >= 4 && cropRect.h >= 4 && (
            <Button size="sm" onClick={applyCrop}>Apply crop</Button>
          )}

          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={undo} title="Undo">
              <Undo2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={resetAll} title="Reset to original">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-center rounded-md bg-muted/30 p-2 max-h-[60vh] overflow-auto">
          <div className="relative inline-block" style={{ lineHeight: 0 }}>
            <canvas
              ref={canvasRef}
              className="max-w-full touch-none rounded-sm shadow-sm"
              style={{ maxHeight: '56vh', cursor: 'crosshair', display: ready ? 'block' : 'none' }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            />
            {!ready && (
              <div className="w-64 h-40 flex items-center justify-center text-sm text-muted-foreground">
                Loading image…
              </div>
            )}
            {mode === 'crop' && cropRect && canvasBox.width > 0 && (
              <div
                className="absolute border-2 border-primary bg-primary/10 pointer-events-none"
                style={{
                  left: `${(cropRect.x / canvasBox.width) * 100}%`,
                  top: `${(cropRect.y / canvasBox.height) * 100}%`,
                  width: `${(cropRect.w / canvasBox.width) * 100}%`,
                  height: `${(cropRect.h / canvasBox.height) * 100}%`,
                }}
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!ready}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
